import debounce from 'lodash.debounce';

import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useField } from 'formik';

import { FormikRawInput, hooks } from '@febos-io/design';

import { identificationNumberParse, identificationNumberUnformat } from '../utils';

export interface FormikInputIdentificationNumberProps {
  id?: string;
  label?: string;
  name: string;
  placeholder?: string;
  type?: string;
  classInput?: string;
  inputProps?: any;
  icon?: string;
  swap?: boolean;
  iconProps?: any;
  disabled?: boolean;
  className?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  onFocus?: (value: string) => void;
}

export function FormikInputIdentificationNumber({
  id: defaultId,
  label,
  name,
  placeholder,
  type = 'text',
  classInput,
  inputProps = {},
  icon,
  swap = false,
  iconProps = {},
  disabled,
  className,
  onChange = () => false,
  onBlur = () => false,
  onFocus = () => false
}: FormikInputIdentificationNumberProps) {
  const [field, meta, helper] = useField(name);
  const { id, idIcon, idFeedback, ariaDescribedby } = hooks.useInput(defaultId, label, icon);

  const [isFocused, setIsFocused] = useState(false);
  const [lastValue, setLastValue] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback(
    debounce((value: any) => {
      if (!isFocused && lastValue !== value) {
        setLastValue(value);
        onChange(value);
      }
    }, 300),
    [isFocused, lastValue]
  );

  const handleBlur = useCallback(
    (event) => {
      setIsFocused(false);
      const newValue = identificationNumberParse(field.value);
      helper.setValue(newValue);
      field.onBlur(event);
      onBlur(newValue);
    },
    [field, helper, onBlur]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    const newValue = identificationNumberUnformat(field.value || '');
    helper.setValue(newValue);
    onFocus(newValue);
  }, [field.value, helper, onFocus]);

  useEffect(() => {
    if (!meta.error && meta.value) {
      handleChange(meta.value);
    }
  }, [meta.value, meta.error, handleChange]);
  return (
    <div className={classNames(className)}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      <FormikRawInput
        id={id}
        placeholder={placeholder}
        ariaDescribedby={ariaDescribedby}
        type={type}
        classInput={classInput}
        inputProps={inputProps}
        icon={icon}
        idIcon={idIcon}
        swap={swap}
        iconProps={iconProps}
        idFeedback={idFeedback}
        disabled={disabled}
        meta={meta}
        {...field}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
    </div>
  );
}

FormikInputIdentificationNumber.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  classInput: PropTypes.string,
  inputProps: PropTypes.object,
  icon: PropTypes.string,
  swap: PropTypes.bool,
  iconProps: PropTypes.object,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func
};

export default FormikInputIdentificationNumber;
